import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import CalendarioReservas from '../components/CalendarioReservas';
import { Calendar, Plus, Edit, Trash2, Clock, MapPin } from 'lucide-react';

const Reservas = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [reservas, setReservas] = useState([]);
  const [areasComuns, setAreasComuns] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCalendario, setShowCalendario] = useState(false);
  const [showCalendarioReservas, setShowCalendarioReservas] = useState(false);
  const [editingReserva, setEditingReserva] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reservasDoDia, setReservasDoDia] = useState([]);
  const [showReservasPopup, setShowReservasPopup] = useState(false);
  const [espacosDisponiveis, setEspacosDisponiveis] = useState([]);
  const [formData, setFormData] = useState({
    area_comum_id: '',
    apartamento_id: '',
    reserva_data_inicio: '',
    reserva_data_fim: '',
    reserva_motivo: '',
    reserva_numero_pessoas: '',
    reserva_observacoes: '',
    reserva_equipamentos_solicitados: []
  });

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadReservas();
      loadAreasComuns();
      loadApartamentos();
    }
  }, [condominioSelecionado]);

  const loadReservas = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) {
        // Não limpar os dados se o condomínio não estiver disponível temporariamente
        console.log('Condomínio não selecionado, mantendo dados existentes');
        return;
      }
      
      const response = await api.get(`/reservas?condominio_id=${condominioId}`);
      setReservas(response.data.data.reservas || []);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAreasComuns = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) {
        setAreasComuns([]);
        return;
      }
      
      const response = await api.get(`/areas-comuns?condominio_id=${condominioId}`);
      setAreasComuns(response.data.data.areasComuns || []);
    } catch (error) {
      console.error('Erro ao carregar áreas comuns:', error);
    }
  };

  const loadApartamentos = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) {
        setApartamentos([]);
        return;
      }
      
      const response = await api.get(`/apartamentos?condominio_id=${condominioId}`);
      setApartamentos(response.data.data.apartamentos || []);
    } catch (error) {
      console.error('Erro ao carregar apartamentos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      if (editingReserva) {
        // Para edição, apenas permitir mudança de status
        await api.put(`/reservas/${editingReserva.reserva_id}/status`, {
          reserva_status: formData.reserva_status
        });
      } else {
        // Adicionar condominio_id aos dados
        const dadosParaEnvio = {
          ...formData,
          condominio_id: getCondominioId()
        };
        await api.post('/reservas', dadosParaEnvio);
      }
      setShowModal(false);
      setEditingReserva(null);
      resetForm();
      loadReservas();
    } catch (error) {
      console.error('Erro ao salvar reserva:', error);
      
      // Tratar diferentes tipos de erro
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Se houver múltiplos erros de validação
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (reserva) => {
    setEditingReserva(reserva);
    setFormData({
      reserva_status: reserva.reserva_status
    });
    setShowModal(true);
  };

  const handleCancel = async (id) => {
    if (window.confirm('Tem certeza que deseja cancelar esta reserva?')) {
      try {
        await api.delete(`/reservas/${id}`, {
          data: { motivo: 'Cancelada pelo usuário' }
        });
        loadReservas();
      } catch (error) {
        console.error('Erro ao cancelar reserva:', error);
      }
    }
  };

  const resetForm = () => {
    // Para moradores, buscar o apartamento do vínculo ativo
    let apartamentoMorador = '';
    
    if (!isAdministrador() && user?.vinculos) {
      // Buscar vínculo ativo do condomínio selecionado
      const vinculoAtivo = user.vinculos.find(v => 
        v.condominio_id === getCondominioId() && v.status === 'ativo'
      );
      apartamentoMorador = vinculoAtivo?.apartamento_id || '';
    } else {
      // Para administradores ou se não houver vínculos
      apartamentoMorador = user?.apartamento_id || '';
    }
    
    setFormData({
      area_comum_id: '',
      apartamento_id: apartamentoMorador,
      reserva_data_inicio: '',
      reserva_data_fim: '',
      reserva_motivo: '',
      reserva_numero_pessoas: '',
      reserva_observacoes: '',
      reserva_equipamentos_solicitados: []
    });
    setErrorMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Se a data de início for alterada, definir a data fim como 23:59 do mesmo dia
      if (name === 'reserva_data_inicio' && value) {
        const dataInicio = new Date(value);
        // Definir para 23:59 do mesmo dia
        dataInicio.setHours(23, 59, 0, 0);
        // Usar formato local para evitar problemas de fuso horário
        const year = dataInicio.getFullYear();
        const month = String(dataInicio.getMonth() + 1).padStart(2, '0');
        const day = String(dataInicio.getDate()).padStart(2, '0');
        const hours = String(dataInicio.getHours()).padStart(2, '0');
        const minutes = String(dataInicio.getMinutes()).padStart(2, '0');
        newData.reserva_data_fim = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      
      return newData;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pendente': 'bg-yellow-100 text-yellow-800',
      'confirmada': 'bg-green-100 text-green-800',
      'em_andamento': 'bg-blue-100 text-blue-800',
      'concluida': 'bg-gray-100 text-gray-800',
      'cancelada': 'bg-red-100 text-red-800',
      'rejeitada': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pendente': 'Pendente',
      'confirmada': 'Confirmada',
      'em_andamento': 'Em Andamento',
      'concluida': 'Concluída',
      'cancelada': 'Cancelada',
      'rejeitada': 'Rejeitada'
    };
    return labels[status] || status;
  };

  // Funções para o calendário de reservas
  const getReservasDoDia = (date) => {
    if (!date) return [];
    
    // Usar formato local para evitar problemas de fuso horário
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return reservas.filter(reserva => {
      const reservaDate = new Date(reserva.reserva_data_inicio);
      const reservaYear = reservaDate.getFullYear();
      const reservaMonth = String(reservaDate.getMonth() + 1).padStart(2, '0');
      const reservaDay = String(reservaDate.getDate()).padStart(2, '0');
      const reservaDateStr = `${reservaYear}-${reservaMonth}-${reservaDay}`;
      return reservaDateStr === dateStr;
    });
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    setSelectedDate(date);
    const reservasDia = getReservasDoDia(date);
    setReservasDoDia(reservasDia);
    
    // Se há reservas, mostrar popup com espaços reservados e disponíveis
    if (reservasDia.length > 0) {
      // Calcular espaços disponíveis (áreas comuns não reservadas)
      const espacosReservados = reservasDia.map(reserva => reserva.area_comum_id);
      const espacosDisponiveis = areasComuns.filter(area => 
        !espacosReservados.includes(area.area_comum_id)
      );
      setEspacosDisponiveis(espacosDisponiveis);
      setShowCalendarioReservas(false); // Fechar calendário
      setShowReservasPopup(true);
    } else {
      // Data disponível - abrir formulário
      setEditingReserva(null);
      resetForm();
      // Definir a data selecionada no formulário
      const dataInicio = new Date(date);
      dataInicio.setHours(9, 0, 0, 0); // Horário padrão 09:00
      const dataFim = new Date(date);
      dataFim.setHours(23, 59, 0, 0);
      
      // Formatar datas no formato local
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      setFormData(prev => ({
        ...prev,
        reserva_data_inicio: formatLocalDateTime(dataInicio),
        reserva_data_fim: formatLocalDateTime(dataFim)
      }));
      
      setShowCalendarioReservas(false);
      setShowModal(true);
    }
  };

  const handleNovaReserva = () => {
    setShowCalendarioReservas(true);
  };

  const handleSelecionarEspaco = (espaco) => {
    // Fechar popup e abrir formulário com espaço selecionado
    setShowReservasPopup(false);
    setEditingReserva(null);
    resetForm();
    
    // Definir a data selecionada no formulário
    const dataInicio = new Date(selectedDate);
    dataInicio.setHours(9, 0, 0, 0); // Horário padrão 09:00
    const dataFim = new Date(selectedDate);
    dataFim.setHours(23, 59, 0, 0);
    
    // Formatar datas no formato local
    const formatLocalDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setFormData(prev => ({
      ...prev,
      area_comum_id: espaco.area_comum_id,
      reserva_data_inicio: formatLocalDateTime(dataInicio),
      reserva_data_fim: formatLocalDateTime(dataFim)
    }));
    
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Reservas</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCalendario(true)}
            className="btn btn-secondary"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Calendário
          </button>
          <button
            onClick={handleNovaReserva}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Reserva
          </button>
        </div>
      </div>

      {/* Lista de Reservas */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Área Comum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Apartamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data/Hora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duração
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reservas.map((reserva) => (
                <tr key={reserva.reserva_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {reserva.areaComum?.area_comum_nome}
                    </div>
                    <div className="text-sm text-gray-500">
                      {reserva.areaComum?.area_comum_tipo}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {reserva.apartamento?.apartamento_bloco} - {reserva.apartamento?.apartamento_numero}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(reserva.reserva_data_inicio)}
                    </div>
                    <div className="text-sm text-gray-500">
                      até {formatDate(reserva.reserva_data_fim)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {reserva.reserva_duracao_horas}h
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {reserva.reserva_valor_total ? formatCurrency(reserva.reserva_valor_total) : 'Gratuito'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reserva.reserva_status)}`}>
                      {getStatusLabel(reserva.reserva_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {['sindico', 'subsindico', 'administrador'].includes(user?.tipo) && (
                        <button
                          onClick={() => handleEdit(reserva)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {(reserva.usuario_id === user?.id || ['sindico', 'subsindico', 'administrador'].includes(user?.tipo)) && 
                       ['pendente', 'confirmada'].includes(reserva.reserva_status) && (
                        <button
                          onClick={() => handleCancel(reserva.reserva_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingReserva ? 'Gerenciar Reserva' : 'Nova Reserva'}
              </h3>
              
              {/* Exibir mensagem de erro */}
              {errorMessage && (
                <div className="mb-4 alert-error border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Erro ao salvar reserva
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {editingReserva ? (
                  // Modal de gerenciamento de status
                  <div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status da Reserva</label>
                      <select
                        name="reserva_status"
                        value={formData.reserva_status}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pendente">Pendente</option>
                        <option value="confirmada">Confirmada</option>
                        <option value="em_andamento">Em Andamento</option>
                        <option value="concluida">Concluída</option>
                        <option value="rejeitada">Rejeitada</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  // Modal de nova reserva
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Área Comum *</label>
                      <select
                        name="area_comum_id"
                        value={formData.area_comum_id}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Selecione a área comum</option>
                        {areasComuns
                          .filter(area => area.area_comum_ativo && area.area_comum_permite_reserva)
                          .map(area => (
                            <option key={area.area_comum_id} value={area.area_comum_id}>
                              {area.area_comum_nome} - {area.area_comum_tipo}
                            </option>
                          ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Apartamento *</label>
                      {isAdministrador() ? (
                        <select
                          name="apartamento_id"
                          value={formData.apartamento_id}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Selecione o apartamento</option>
                          {apartamentos.map(apt => (
                            <option key={apt.apartamento_id} value={apt.apartamento_id}>
                              {apt.apartamento_bloco} - {apt.apartamento_numero}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={apartamentos.find(apt => apt.apartamento_id == formData.apartamento_id) 
                            ? `${apartamentos.find(apt => apt.apartamento_id == formData.apartamento_id).apartamento_bloco} - ${apartamentos.find(apt => apt.apartamento_id == formData.apartamento_id).apartamento_numero}`
                            : 'Carregando...'}
                          readOnly
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                        />
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Data/Hora Início *</label>
                        <input
                          type="datetime-local"
                          name="reserva_data_inicio"
                          value={formData.reserva_data_inicio}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      {/* Campo de data fim oculto - será preenchido automaticamente */}
                      <input
                        type="hidden"
                        name="reserva_data_fim"
                        value={formData.reserva_data_fim}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Motivo da Reserva *</label>
                      <input
                        type="text"
                        name="reserva_motivo"
                        value={formData.reserva_motivo}
                        onChange={handleInputChange}
                        required
                        placeholder="Ex: Aniversário, Churrasco, Reunião..."
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Número de Pessoas</label>
                      <input
                        type="number"
                        name="reserva_numero_pessoas"
                        value={formData.reserva_numero_pessoas}
                        onChange={handleInputChange}
                        min="1"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Observações</label>
                      <textarea
                        name="reserva_observacoes"
                        value={formData.reserva_observacoes}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Informações adicionais sobre a reserva..."
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingReserva(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-md text-white ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Salvando...' : (editingReserva ? 'Atualizar Status' : 'Criar Reserva')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Popup de Reservas Existentes */}
      {showReservasPopup && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <Calendar className="h-6 w-6 text-red-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Reservas em {selectedDate.toLocaleDateString('pt-BR')}
                </h2>
              </div>
              <button
                onClick={() => setShowReservasPopup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="mb-4">
                <p className="text-gray-600">
                  Esta data possui reservas. Você pode reservar um espaço disponível ou escolher outra data:
                </p>
              </div>

              {/* Espaços Reservados */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Espaços Reservados</h3>
                <div className="space-y-3">
                  {reservasDoDia.map(reserva => (
                    <div
                      key={reserva.reserva_id}
                      className={`p-4 rounded-lg border ${getStatusColor(reserva.reserva_status)}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4" />
                          <span className="font-medium text-sm">
                            {reserva.areaComum?.area_comum_nome || 'N/A'}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(reserva.reserva_status)}`}>
                          {getStatusLabel(reserva.reserva_status)}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatDate(reserva.reserva_data_inicio)}</span>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <strong>Apartamento:</strong> {reserva.apartamento?.apartamento_bloco || 'N/A'} - {reserva.apartamento?.apartamento_numero || 'N/A'}
                      </div>
                      
                      {reserva.reserva_motivo && (
                        <div className="text-sm text-gray-600 mt-1">
                          <strong>Motivo:</strong> {reserva.reserva_motivo}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Espaços Disponíveis */}
              {espacosDisponiveis.length > 0 ? (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Espaços Disponíveis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {espacosDisponiveis.map(espaco => (
                      <div
                        key={espaco.area_comum_id}
                        className="p-4 border border-green-200 rounded-lg bg-green-50 hover:bg-green-100 cursor-pointer transition-colors"
                        onClick={() => handleSelecionarEspaco(espaco)}
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <MapPin className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-sm text-green-800">
                            {espaco.area_comum_nome}
                          </span>
                        </div>
                        <p className="text-xs text-green-600">
                          Clique para reservar este espaço
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-sm font-medium text-red-800">
                        Todos os espaços estão reservados para esta data
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      Escolha outra data para fazer sua reserva
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowReservasPopup(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Fechar
                </button>
                <button
                  onClick={() => {
                    setShowReservasPopup(false);
                    setShowCalendarioReservas(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Escolher Outra Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do Calendário de Reservas para Nova Reserva */}
      {showCalendarioReservas && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <Calendar className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Selecionar Data para Nova Reserva</h2>
              </div>
              <button
                onClick={() => setShowCalendarioReservas(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Calendário */}
            <div className="p-6">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 35 }, (_, index) => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                  const startingDayOfWeek = firstDay.getDay();
                  const dayNumber = index - startingDayOfWeek + 1;
                  const date = new Date(today.getFullYear(), today.getMonth(), dayNumber);
                  
                  if (dayNumber < 1 || dayNumber > new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()) {
                    return <div key={index} className="min-h-[60px] p-2"></div>;
                  }

                  const dayReservas = getReservasDoDia(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isPast = date < new Date().setHours(0, 0, 0, 0);
                  
                  return (
                    <div
                      key={index}
                      className={`
                        min-h-[60px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50
                        ${isToday ? 'bg-blue-50 border-blue-300' : ''}
                        ${isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
                      `}
                      onClick={() => !isPast && handleDateClick(date)}
                    >
                      <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                        {dayNumber}
                      </div>
                      {dayReservas.length > 0 && !isPast && (
                        <div className="mt-1">
                          <div className="w-2 h-2 bg-red-500 rounded-full mx-auto" title={`${dayReservas.length} reserva(s)`}></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do Calendário */}
      {showCalendario && (
        <CalendarioReservas onClose={() => setShowCalendario(false)} />
      )}
    </div>
  );
};

export default Reservas;
